import React, { useState, useEffect } from "react";

const NimGame = () => {
  const [piles, setPiles] = useState([1, 3, 5, 7]);
  const [customPiles, setCustomPiles] = useState([1, 3, 5, 7]); // 自定义配置
  const [currentPlayer, setCurrentPlayer] = useState(1); // 1 or 2
  const [gameMode, setGameMode] = useState("pvp"); // 'pvp', 'ai-easy', 'ai-hard'
  const [gameStatus, setGameStatus] = useState("playing"); // 'playing', 'finished'
  const [winner, setWinner] = useState(null);
  const [selectedMatches, setSelectedMatches] = useState([]); // [{pileIndex, matchIndex}]
  const [nimSum, setNimSum] = useState(0);
  const [aiMoveMessage, setAiMoveMessage] = useState("");
  const [flashingMatches, setFlashingMatches] = useState([]); // 电脑选择的火柴棒闪烁
  
  // 新增状态
  const [showSettings, setShowSettings] = useState(false);
  const [displayMode, setDisplayMode] = useState("matches"); // 'matches' or 'coins'
  const [playerFirst, setPlayerFirst] = useState(true); // 人机对战时玩家是否先手
  const [errorMessage, setErrorMessage] = useState(""); // 错误提示
  const [showError, setShowError] = useState(false);

  // 计算异或和
  const calculateNimSum = (pilesArray) => {
    return pilesArray.reduce((sum, pile) => sum ^ pile, 0);
  };

  // 更新异或和
  useEffect(() => {
    setNimSum(calculateNimSum(piles));
  }, [piles]);

  // 检查游戏是否结束
  useEffect(() => {
    const totalSticks = piles.reduce((sum, pile) => sum + pile, 0);
    if (totalSticks === 0) {
      setGameStatus("finished");
      setWinner(currentPlayer === 1 ? 2 : 1); // 上一个玩家获胜
    }
  }, [piles, currentPlayer]);

  // 显示错误提示
  const showErrorMessage = (message) => {
    setErrorMessage(message);
    setShowError(true);
    setTimeout(() => setShowError(false), 3000);
  };

  // 电脑AI移动
  const makeAIMove = () => {
    if (gameMode === "ai-easy") {
      makeRandomMove();
    } else if (gameMode === "ai-hard") {
      makeOptimalMove();
    }
  };

  // 随机移动
  const makeRandomMove = () => {
    const availablePiles = piles
      .map((pile, index) => ({ pile, index }))
      .filter((item) => item.pile > 0);

    if (availablePiles.length === 0) return;

    const randomPileIndex = Math.floor(Math.random() * availablePiles.length);
    const pileIndex = availablePiles[randomPileIndex].index;
    const maxTake = piles[pileIndex];
    const takeAmount = Math.floor(Math.random() * maxTake) + 1;

    makeAIMove_withAnimation(pileIndex, takeAmount, "电脑随机选择");
  };

  // 最优移动
  const makeOptimalMove = () => {
    const currentNimSum = calculateNimSum(piles);

    // 如果当前异或和为0，随机移动（已经是必败态）
    if (currentNimSum === 0) {
      makeRandomMove();
      return;
    }

    // 寻找能让异或和变为0的移动
    for (let i = 0; i < piles.length; i++) {
      if (piles[i] === 0) continue;

      const targetSize = piles[i] ^ currentNimSum;
      if (targetSize < piles[i]) {
        const takeAmount = piles[i] - targetSize;
        makeAIMove_withAnimation(i, takeAmount, "使用最优策略");
        return;
      }
    }

    // 如果找不到最优移动，随机移动
    makeRandomMove();
  };

  // 带动画的电脑移动
  const makeAIMove_withAnimation = (pileIndex, amount, strategy) => {
    // 计算要闪烁的火柴棒（随机选择位置）
    const availableIndexes = [];
    for (let i = 0; i < piles[pileIndex]; i++) {
      availableIndexes.push(i);
    }

    // 随机选择要拿取的火柴棒位置
    const flashMatches = [];
    const shuffled = [...availableIndexes].sort(() => Math.random() - 0.5);
    for (let i = 0; i < amount; i++) {
      flashMatches.push({ pileIndex, matchIndex: shuffled[i] });
    }

    setFlashingMatches(flashMatches);
    setAiMoveMessage(
      `电脑从第${pileIndex + 1}行中拿走了${amount}个${displayMode === 'matches' ? '火柴棒' : '硬币'} (${strategy})`
    );

    // 闪烁3次后移除
    setTimeout(() => {
      makeMove(pileIndex, amount);
      setFlashingMatches([]);
    }, 1800); // 3次闪烁 * 600ms
  };

  // 执行移动
  const makeMove = (pileIndex, amount) => {
    const newPiles = [...piles];
    newPiles[pileIndex] = Math.max(0, newPiles[pileIndex] - amount);
    setPiles(newPiles);
    setCurrentPlayer(currentPlayer === 1 ? 2 : 1);
    setSelectedMatches([]);
  };

  // 处理火柴棒点击
  const handleMatchClick = (pileIndex, matchIndex) => {
    if (gameStatus === "finished") return;
    if (gameMode !== "pvp" && currentPlayer === 2) return; // 电脑回合时不允许点击
    if (flashingMatches.length > 0) return; // 电脑移动动画期间不允许点击

    // 检查是否与已选择的堆冲突
    if (
      selectedMatches.length > 0 &&
      selectedMatches[0].pileIndex !== pileIndex
    ) {
      showErrorMessage(
        `只能从同一行中拿取${displayMode === 'matches' ? '火柴棒' : '硬币'}！请先取消当前选择，或继续选择第${
          selectedMatches[0].pileIndex + 1
        }行的${displayMode === 'matches' ? '火柴棒' : '硬币'}。`
      );
      return;
    }

    const matchKey = `${pileIndex}-${matchIndex}`;
    const isSelected = selectedMatches.some(
      (m) => `${m.pileIndex}-${m.matchIndex}` === matchKey
    );

    if (isSelected) {
      // 取消选择
      setSelectedMatches(
        selectedMatches.filter(
          (m) => `${m.pileIndex}-${m.matchIndex}` !== matchKey
        )
      );
    } else {
      // 添加选择
      setSelectedMatches([...selectedMatches, { pileIndex, matchIndex }]);
    }
  };

  // 验证选择是否合法
  const validateSelection = () => {
    if (selectedMatches.length === 0)
      return { valid: false, message: `请选择至少一个${displayMode === 'matches' ? '火柴棒' : '硬币'}` };

    // 只需要检查是否都来自同一行，无需检查连续性
    const pileIndexes = [...new Set(selectedMatches.map((m) => m.pileIndex))];
    if (pileIndexes.length > 1) {
      return { valid: false, message: `只能从同一行中拿取${displayMode === 'matches' ? '火柴棒' : '硬币'}！` };
    }

    return { valid: true, message: "" };
  };

  // 确认玩家移动
  const confirmPlayerMove = () => {
    const validation = validateSelection();
    if (!validation.valid) {
      showErrorMessage(validation.message);
      return;
    }

    const pileIndex = selectedMatches[0].pileIndex;
    const amount = selectedMatches.length;
    makeMove(pileIndex, amount);
    setAiMoveMessage(""); // 清除AI移动消息
  };

  // 电脑回合
  useEffect(() => {
    if (
      gameStatus === "playing" &&
      (gameMode === "ai-easy" || gameMode === "ai-hard") &&
      currentPlayer === 2
    ) {
      const timer = setTimeout(() => {
        makeAIMove();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [currentPlayer, gameStatus, gameMode]);

  // 重新开始游戏
  const resetGame = () => {
    // 确保使用有效的配置值
    const validatedPiles = customPiles.map(pile => Math.max(1, parseInt(pile) || 1));
    setPiles([...validatedPiles]);
    setCurrentPlayer(
      gameMode === "pvp" ? 1 : playerFirst ? 1 : 2
    );
    setGameStatus("playing");
    setWinner(null);
    setSelectedMatches([]);
    setAiMoveMessage("");
    setFlashingMatches([]);
  };

  // 应用自定义设置
  const applySettings = () => {
    // 确保所有值都是有效的
    const validatedPiles = customPiles.map(pile => Math.max(1, parseInt(pile) || 1));
    setCustomPiles(validatedPiles);
    setPiles([...validatedPiles]);
    setShowSettings(false);
    resetGame();
  };

  // 添加新行
  const addNewRow = () => {
    setCustomPiles([...customPiles, 1]);
  };

  // 删除行
  const removeRow = (index) => {
    if (customPiles.length > 1) {
      setCustomPiles(customPiles.filter((_, i) => i !== index));
    }
  };

  // 更新行的数量
  const updateRowCount = (index, value) => {
    const newPiles = [...customPiles];
    newPiles[index] = value; // 允许输入过程中的任何值
    setCustomPiles(newPiles);
  };

  // 验证并修正输入值（失去焦点时）
  const validateRowCount = (index) => {
    const newPiles = [...customPiles];
    const currentValue = newPiles[index];
    const validValue = Math.max(1, parseInt(currentValue) || 1);
    newPiles[index] = validValue;
    setCustomPiles(newPiles);
  };

  // 渲染火柴棒/硬币 - 按行排列
  const renderMatchRow = (count, pileIndex) => {
    return (
      <div className="flex justify-center items-center gap-2 mb-4">
        <div className="w-16 text-right text-sm font-medium text-gray-600">
          第{pileIndex + 1}行:
        </div>
        <div className="flex justify-center gap-1 flex-wrap">
          {Array.from({ length: count }, (_, index) => {
            const matchIndex = index;
            const isSelected = selectedMatches.some(
              (m) => m.pileIndex === pileIndex && m.matchIndex === matchIndex
            );
            const isFlashing = flashingMatches.some(
              (m) => m.pileIndex === pileIndex && m.matchIndex === matchIndex
            );

            if (displayMode === "coins") {
              return (
                <div
                  key={index}
                  className={`w-8 h-8 rounded-full cursor-pointer transition-all transform hover:scale-110 ${
                    isSelected ? "ring-2 ring-blue-500 ring-offset-1" : ""
                  } ${isFlashing ? "animate-pulse" : ""}`}
                  style={{
                    background: isSelected
                      ? "radial-gradient(circle at 30% 30%, #60a5fa, #1d4ed8)"
                      : isFlashing
                      ? "radial-gradient(circle at 30% 30%, #f87171, #dc2626)"
                      : "radial-gradient(circle at 30% 30%, #fbbf24, #d97706)",
                    animation: isFlashing ? "flash 0.6s infinite" : "none",
                    border: "2px solid #92400e",
                    boxShadow: isSelected || isFlashing ? "0 4px 8px rgba(0,0,0,0.3)" : "0 2px 4px rgba(0,0,0,0.2)",
                  }}
                  onClick={() => handleMatchClick(pileIndex, matchIndex)}
                >
                  <div className="w-full h-full flex items-center justify-center">
                  </div>
                </div>
              );
            } else {
              return (
                <div
                  key={index}
                  className={`w-4 h-16 rounded-sm cursor-pointer transition-all transform hover:scale-105 ${
                    isSelected ? "ring-2 ring-blue-500 ring-offset-1" : ""
                  } ${isFlashing ? "animate-pulse" : ""}`}
                  style={{
                    background: isSelected
                      ? "linear-gradient(to bottom, #3b82f6 0%, #1d4ed8 100%)"
                      : isFlashing
                      ? "linear-gradient(to bottom, #ef4444 0%, #dc2626 100%)"
                      : "linear-gradient(to bottom, #f59e0b 0%, #d97706 100%)",
                    animation: isFlashing ? "flash 0.6s infinite" : "none",
                  }}
                  onClick={() => handleMatchClick(pileIndex, matchIndex)}
                >
                  <div
                    className={`w-3 h-3 rounded-full mx-auto mt-1 ${
                      isSelected
                        ? "bg-blue-200"
                        : isFlashing
                        ? "bg-red-200"
                        : "bg-red-500"
                    }`}
                  ></div>
                </div>
              );
            }
          })}
        </div>
        <div className="w-16 text-left text-sm text-gray-500">{count}个</div>
      </div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-green-800 min-h-screen">
      <style jsx>{`
        @keyframes flash {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.3;
          }
        }
        @keyframes slideIn {
          from {
            transform: translateY(10px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        @keyframes slideOut {
          from {
            transform: translateY(0);
            opacity: 1;
          }
          to {
            transform: translateY(-10px);
            opacity: 0;
          }
        }
        .slide-in {
          animation: slideIn 0.3s ease-out;
        }
        .slide-out {
          animation: slideOut 0.3s ease-in;
        }
      `}</style>



      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-green-800">
            NIM 游戏探究
          </h1>
          <button
            onClick={() => setShowSettings(true)}
            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 flex items-center gap-2"
          >
            ⚙️ 游戏设置
          </button>
        </div>

        {/* 游戏模式选择 */}
        <div className="mb-6 text-center">
          <div className="flex justify-center gap-4">
            <button
              onClick={() => {
                setGameMode("pvp");
                resetGame();
              }}
              className={`px-4 py-2 rounded ${
                gameMode === "pvp" ? "bg-blue-500 text-white" : "bg-gray-200"
              }`}
            >
              人与人对战
            </button>
            <button
              onClick={() => {
                setGameMode("ai-easy");
                resetGame();
              }}
              className={`px-4 py-2 rounded ${
                gameMode === "ai-easy"
                  ? "bg-blue-500 text-white"
                  : "bg-gray-200"
              }`}
            >
              人机对战(普通)
            </button>
            <button
              onClick={() => {
                setGameMode("ai-hard");
                resetGame();
              }}
              className={`px-4 py-2 rounded ${
                gameMode === "ai-hard"
                  ? "bg-blue-500 text-white"
                  : "bg-gray-200"
              }`}
            >
              人机对战(高级)
            </button>
          </div>
        </div>

        {/* AI移动消息 */}
        {aiMoveMessage && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-blue-800 font-medium">💻 {aiMoveMessage}</p>
          </div>
        )}

        {/* 游戏状态 */}
        <div className="text-center mb-6">
          {gameStatus === "playing" ? (
            <div>
              <p className="text-xl mb-2">
                轮到:{" "}
                <span className="font-bold text-blue-600">
                  {gameMode === "pvp"
                    ? `玩家 ${currentPlayer}`
                    : currentPlayer === 1
                    ? "玩家"
                    : "电脑"}
                </span>
              </p>
              {(gameMode === "ai-easy" || gameMode === "ai-hard") &&
                currentPlayer === 2 && (
                  <p className="text-gray-600">电脑思考中...</p>
                )}
              {selectedMatches.length > 0 && (
                <p className="text-sm text-gray-600 mt-1">
                  已选择第{selectedMatches[0].pileIndex + 1}行的{" "}
                  {selectedMatches.length} 个{displayMode === 'matches' ? '火柴棒' : '硬币'}
                </p>
              )}
            </div>
          ) : (
            <div className="text-2xl font-bold">
              🎉{" "}
              {gameMode === "pvp"
                ? `玩家 ${winner}`
                : winner === 1
                ? "玩家"
                : "电脑"}{" "}
              获胜！
            </div>
          )}
        </div>

        {/* 游戏区域 - 按行排列 */}
        <div className="p-6 bg-green-50 rounded-lg mb-6">
          <div className="max-w-2xl mx-auto">
            {piles.map((pile, index) => (
              <div key={index}>{pile > 0 && renderMatchRow(pile, index)}</div>
            ))}
          </div>
        </div>

        {/* 错误提示 - 显示在游戏区域下方 */}
        {showError && (
          <div className="mb-4 flex justify-center">
            <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-3 rounded-lg shadow-lg flex items-center gap-2 slide-in max-w-md">
              <span className="text-xl">⚠️</span>
              <span className="text-sm font-medium">{errorMessage}</span>
            </div>
          </div>
        )}

        {/* 操作按钮 */}
        {gameStatus === "playing" &&
          selectedMatches.length > 0 &&
          (gameMode === "pvp" ||
            (gameMode !== "pvp" && currentPlayer === 1)) && (
            <div className="flex justify-center gap-4 mb-4">
              <button
                onClick={confirmPlayerMove}
                className="px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 font-bold"
              >
                确认拿取 ({selectedMatches.length} 个)
              </button>
              <button
                onClick={() => setSelectedMatches([])}
                className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
              >
                取消选择
              </button>
            </div>
          )}

        {/* 重新开始 */}
        <div className="text-center mb-6">
          <button
            onClick={resetGame}
            className="px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 font-bold"
          >
            重新开始
          </button>
        </div>

        {/* 游戏规则 */}
        <div className="p-4 bg-gray-50 rounded-lg">
          <h3 className="font-bold mb-2">游戏规则:</h3>
          <ul className="text-sm space-y-1">
            <li>• 点击{displayMode === 'matches' ? '火柴棒' : '硬币'}来选择，选中的会变蓝色</li>
            <li>• 只能从同一行中拿取</li>
            <li>• 每次至少要取1个，可以取完整行</li>
            <li>• 取走最后一个{displayMode === 'matches' ? '火柴棒' : '硬币'}的玩家获胜</li>
          </ul>
        </div>
      </div>

      {/* 设置弹窗 */}
      {showSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h2 className="text-2xl font-bold mb-4">游戏设置</h2>
            
            {/* 显示模式 */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                显示模式
              </label>
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="matches"
                    checked={displayMode === "matches"}
                    onChange={(e) => setDisplayMode(e.target.value)}
                    className="mr-2"
                  />
                  火柴棒
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="coins"
                    checked={displayMode === "coins"}
                    onChange={(e) => setDisplayMode(e.target.value)}
                    className="mr-2"
                  />
                  硬币
                </label>
              </div>
            </div>

            {/* 先手设置 */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                人机对战先手设置
              </label>
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    checked={playerFirst}
                    onChange={() => setPlayerFirst(true)}
                    className="mr-2"
                  />
                  玩家先手
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    checked={!playerFirst}
                    onChange={() => setPlayerFirst(false)}
                    className="mr-2"
                  />
                  电脑先手
                </label>
              </div>
            </div>

            {/* 自定义行配置 */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                自定义游戏配置
              </label>
              {customPiles.map((count, index) => (
                <div key={index} className="flex items-center gap-2 mb-2">
                  <span className="w-16 text-sm">第{index + 1}行:</span>
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={count}
                    onChange={(e) => updateRowCount(index, e.target.value)}
                    onBlur={() => validateRowCount(index)}
                    placeholder="输入数量"
                    className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:border-blue-500 focus:outline-none"
                  />
                  <span className="text-sm text-gray-500">个</span>
                  {customPiles.length > 1 && (
                    <button
                      onClick={() => removeRow(index)}
                      className="px-2 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600"
                    >
                      删除
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={addNewRow}
                className="w-full px-3 py-2 bg-green-500 text-white rounded hover:bg-green-600 text-sm"
              >
                + 添加新行
              </button>
            </div>

            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowSettings(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                取消
              </button>
              <button
                onClick={applySettings}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                应用设置
              </button>
            </div>
          </div>
        </div>
      )}

      <footer className="mt-8 w-full text-center text-sm text-gray-500">
        © {new Date().getFullYear()} 孙维刚教育研究院-陈硕老师 保留所有权利。
      </footer>
    </div>
  );
};

export default NimGame;
